import { useMemo, useState } from 'react';
import type { MonthlyTicketItem, EstadoOt } from '@sime/shared';
import { ALL_STATES, STATE_COLORS, STATE_LABELS } from '@sime/shared';
import { cn } from '@/lib/utils';
import { TicketCard } from './ticket-card';

export type DayCellDensity = 'compact' | 'medium';

interface StatusSegment {
  estado: EstadoOt;
  count: number;
  color: string;
}

/**
 * Computes ordered (by ALL_STATES) non-zero status segments for the cell's
 * mini-bar visualization.
 */
function buildStatusSegments(tickets: MonthlyTicketItem[]): StatusSegment[] {
  if (tickets.length === 0) return [];
  const counts = new Map<EstadoOt, number>();
  for (const t of tickets) {
    counts.set(t.estadoOt, (counts.get(t.estadoOt) ?? 0) + 1);
  }
  return ALL_STATES.flatMap((estado) => {
    const count = counts.get(estado) ?? 0;
    if (count === 0) return [];
    return [{ estado, count, color: STATE_COLORS[estado].bg }];
  });
}

interface DayCellProps {
  date: string;
  dayNumber: number;
  tickets: MonthlyTicketItem[];
  isToday: boolean;
  isCurrentMonth: boolean;
  activeFilters: Set<string>;
  isRescheduling?: boolean;
  /**
   * Visual density. `compact` (default) is the monthly grid look — small
   * padding, tiny day number, ticket overflow scrolls. `medium` is for the
   * weekly view's 7-cell row — more padding, day-of-week label visible.
   */
  density?: DayCellDensity;
  onDrop: (ticketId: number, targetDate: string) => void;
  onDragStart: (ticketId: number, estado: string) => void;
}

export function DayCell({
  date,
  dayNumber,
  tickets,
  isToday,
  isCurrentMonth,
  activeFilters,
  isRescheduling = false,
  density = 'compact',
  onDrop,
  onDragStart,
}: DayCellProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const isMedium = density === 'medium';

  const visibleTickets = tickets.filter((t) => activeFilters.has(t.estadoOt));

  // Mini-bar shows the per-status mix using the *visible* (post-filter) tickets
  // so toggling a status filter immediately reshapes the bar.
  const segments = useMemo(() => buildStatusSegments(visibleTickets), [visibleTickets]);
  const segmentTotal = segments.reduce((acc, s) => acc + s.count, 0);
  const segmentLabel = segments
    .map((s) => `${STATE_LABELS[s.estado]}: ${s.count}`)
    .join(', ');

  const handleDragOver = (e: React.DragEvent) => {
    if (isRescheduling) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const ticketIdStr = e.dataTransfer.getData('text/plain');
    const ticketId = Number(ticketIdStr);
    if (ticketId > 0) {
      onDrop(ticketId, date);
    }
  };

  /**
   * Arrow-key navigation between cells. Uses sibling DOM query (role=gridcell)
   * relative to the current cell so we don't need a parent-managed focus map.
   * Up/Down moves ±7 cells (a row), Left/Right moves ±1.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Only handle when the focus is on the cell itself, not on a child ticket.
    if (e.target !== e.currentTarget) return;
    const dir = (
      e.key === 'ArrowLeft' ? -1 :
      e.key === 'ArrowRight' ? 1 :
      e.key === 'ArrowUp' ? -7 :
      e.key === 'ArrowDown' ? 7 :
      e.key === 'Home' ? 'home' :
      e.key === 'End' ? 'end' :
      null
    );
    if (dir === null) return;
    e.preventDefault();
    const grid = e.currentTarget.closest('[role="grid"]');
    if (!grid) return;
    const cells = Array.from(grid.querySelectorAll<HTMLElement>('[role="gridcell"]'));
    const idx = cells.indexOf(e.currentTarget);
    if (idx === -1) return;
    let next = idx;
    if (dir === 'home') next = 0;
    else if (dir === 'end') next = cells.length - 1;
    else next = idx + dir;
    if (next < 0 || next >= cells.length) return;
    cells[next]?.focus();
  };

  return (
    <div
      role="gridcell"
      tabIndex={0}
      aria-current={isToday ? 'date' : undefined}
      aria-label={`${date}${isToday ? ' (hoy)' : ''}${visibleTickets.length > 0 ? `, ${visibleTickets.length} tickets` : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg transition-all duration-150',
        'bg-sime-surface shadow-neu-xs',
        // Keyboard focus ring (only visible on keyboard nav, not mouse click)
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-sime-accent focus-visible:ring-offset-1',
        isMedium ? 'p-2' : 'p-1',
        isCurrentMonth
          ? 'border border-sime-border-subtle/30'
          : 'border border-sime-border-subtle/20 opacity-40',
        isToday && 'ring-2 ring-blue-500 ring-offset-1 shadow-neu-sm',
        isDragOver && !isToday && 'bg-blue-50 dark:bg-blue-900/20 shadow-neu-inset-xs',
        isDragOver && isToday && 'bg-blue-50 dark:bg-blue-900/20',
      )}
    >
      {/* Day number + count */}
      <div className={cn('flex items-center justify-between px-0.5', isMedium ? 'mb-1' : 'mb-0.5')}>
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full font-bold',
            isMedium ? 'h-7 w-7 text-xs' : 'h-5 w-5 text-[10px]',
            isToday
              ? 'bg-blue-500 text-white'
              : 'text-sime-text-secondary',
          )}
        >
          {dayNumber}
        </span>

        {visibleTickets.length > 0 ? (
          <span className={cn('font-semibold text-sime-text-tertiary', isMedium ? 'text-[11px]' : 'text-[9px]')}>
            {visibleTickets.length}
          </span>
        ) : null}
      </div>

      {/* Color-coded status mini-bar — segments are proportional to per-status counts. */}
      {segments.length > 0 && segmentTotal > 0 ? (
        <div
          className={cn(
            'flex w-full overflow-hidden rounded-full bg-sime-border-subtle/40',
            isMedium ? 'h-[3px] mb-1' : 'h-[2px] mb-0.5',
          )}
          role="img"
          aria-label={`Estados: ${segmentLabel}`}
          title={segmentLabel}
        >
          {segments.map((s) => (
            <span
              key={s.estado}
              className="h-full"
              style={{
                width: `${(s.count / segmentTotal) * 100}%`,
                backgroundColor: s.color,
              }}
            />
          ))}
        </div>
      ) : null}

      {/* Ticket list — scrollable, fills cell */}
      <div className={cn('flex flex-col overflow-y-auto flex-1 min-h-0', isMedium ? 'gap-1' : 'gap-[2px]')}>
        {visibleTickets.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            isRescheduling={isRescheduling}
            onDragStart={onDragStart}
          />
        ))}
      </div>

      {/* Drop target hint */}
      {isDragOver ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/80 dark:bg-blue-900/20">
          <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
            Soltar aqui
          </span>
        </div>
      ) : null}
    </div>
  );
}
